import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { Zap, Timer, Trophy, RotateCcw, CheckCircle } from 'lucide-react';

const SpeedGame = () => {
  const { t } = useTranslation();
  const [target, setTarget] = useState(null);
  const [score, setScore] = useState(0);
  const [level, setLevel] = useState(1);
  const [time, setTime] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isCompleted, setIsCompleted] = useState(false);
  const [reactionTime, setReactionTime] = useState(0);
  const [startTime, setStartTime] = useState(0);

  const targets = ['⚡', '🧠', '💻', '🔮', '🎯', '🚀', '🌟', '💎', '🎮', '🔋'];

  const generateTarget = useCallback(() => {
    const randomTarget = targets[Math.floor(Math.random() * targets.length)];
    setTarget(randomTarget);
    setReactionTime(0);
    setStartTime(0);
  }, []);

  const startGame = () => {
    setIsPlaying(true);
    setScore(0);
    setTime(0);
    setLevel(1);
    setIsCompleted(false);
    generateTarget();
  };

  const handleTargetClick = () => {
    if (!isPlaying || !target) return;
    
    const endTime = Date.now();
    const reaction = endTime - startTime;
    setReactionTime(reaction);
    
    // Calculate score based on reaction time and level
    const baseScore = 100;
    const timeBonus = Math.max(0, 100 - Math.floor(reaction / 100));
    const levelBonus = level * 50;
    const newScore = baseScore + timeBonus + levelBonus;
    
    setScore(prev => prev + newScore);
    
    // Check if level should increase
    if (score + newScore >= level * 500) {
      if (level < 5) {
        setLevel(prev => prev + 1);
      } else {
        setIsCompleted(true);
        setIsPlaying(false);
      }
    }
    
    // Generate new target after a short delay
    setTimeout(() => {
      generateTarget();
    }, 1000);
  };

  const resetGame = () => {
    setIsPlaying(false);
    setTarget(null);
    setScore(0);
    setLevel(1);
    setTime(0);
    setReactionTime(0);
    setStartTime(0);
  };

  useEffect(() => {
    let interval;
    if (isPlaying && !isCompleted) {
      interval = setInterval(() => {
        setTime(prev => prev + 1);
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [isPlaying, isCompleted]);

  useEffect(() => {
    if (target && isPlaying) {
      // Random delay before showing target
      const delay = Math.random() * 2000 + 1000; // 1-3 seconds
      const timer = setTimeout(() => {
        setStartTime(Date.now());
      }, delay);
      
      return () => clearTimeout(timer);
    }
  }, [target, isPlaying]);

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  const getAverageReactionTime = () => {
    return reactionTime > 0 ? `${reactionTime}ms` : 'N/A';
  };

  return (
    <div className="max-w-4xl mx-auto">
      {/* Game Header */}
      <div className="cyber-card p-6 mb-8">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
          <div className="flex items-center justify-center space-x-2">
            <Zap className="h-6 w-6 text-neon-blue" />
            <div>
              <p className="text-sm text-gray-400 font-tech">{t('game.score')}</p>
              <p className="text-2xl font-cyber font-bold text-neon-blue">{score}</p>
            </div>
          </div>
          
          <div className="flex items-center justify-center space-x-2">
            <Trophy className="h-6 w-6 text-neon-green" />
            <div>
              <p className="text-sm text-gray-400 font-tech">{t('game.level')}</p>
              <p className="text-2xl font-cyber font-bold text-neon-green">{level}</p>
            </div>
          </div>
          
          <div className="flex items-center justify-center space-x-2">
            <Timer className="h-6 w-6 text-neon-pink" />
            <div>
              <p className="text-sm text-gray-400 font-tech">{t('game.time')}</p>
              <p className="text-2xl font-cyber font-bold text-neon-pink">{formatTime(time)}</p>
            </div>
          </div>
          
          <div className="flex items-center justify-center space-x-2">
            <RotateCcw className="h-6 w-6 text-neon-yellow" />
            <button
              onClick={resetGame}
              className="cyber-button text-sm px-4 py-2"
            >
              Reset
            </button>
          </div>
        </div>
      </div>

      {/* Game Instructions */}
      {!isPlaying && !isCompleted && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="cyber-card p-6 mb-8 text-center"
        >
          <h3 className="text-2xl font-cyber font-bold text-white mb-4">
            Speed Test Challenge
          </h3>
          <p className="text-gray-300 font-tech mb-6">
            Test your reaction speed! Click the target symbol as quickly as possible when it appears.
            Faster reactions earn higher scores and level up faster!
          </p>
          <button
            onClick={startGame}
            className="cyber-button text-lg px-8 py-3"
          >
            Start Game
          </button>
        </motion.div>
      )}

      {/* Completion Message */}
      {isCompleted && (
        <motion.div
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          className="cyber-card p-6 mb-8 text-center border-neon-green/50"
        >
          <CheckCircle className="h-16 w-16 text-neon-green mx-auto mb-4" />
          <h3 className="text-3xl font-cyber font-bold text-neon-green mb-4">
            All Levels Completed!
          </h3>
          <p className="text-gray-300 font-tech mb-4">
            Final Score: {score} | Total Time: {formatTime(time)}
          </p>
          <button
            onClick={resetGame}
            className="cyber-button text-lg px-6 py-3"
          >
            Play Again
          </button>
        </motion.div>
      )}

      {/* Game Area */}
      {isPlaying && (
        <div className="cyber-card p-6 mb-8">
          <h4 className="text-xl font-cyber font-bold text-white mb-6 text-center">
            Click the target when it appears!
          </h4>
          
          {/* Target Display */}
          <div className="relative h-64 bg-dark-100/50 rounded-lg border border-neon-blue/30 mb-6 flex items-center justify-center">
            {target && startTime > 0 ? (
              <motion.div
                initial={{ opacity: 0, scale: 0.5 }}
                animate={{ opacity: 1, scale: 1 }}
                className="text-8xl cursor-pointer hover:scale-110 transition-transform duration-200"
                onClick={handleTargetClick}
              >
                {target}
              </motion.div>
            ) : (
              <div className="text-gray-500 font-tech text-center">
                {target ? 'Get ready...' : 'Loading...'}
              </div>
            )}
          </div>

          {/* Game Stats */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-center">
            <div>
              <p className="text-gray-400 font-tech">Reaction Time</p>
              <p className="text-2xl font-cyber font-bold text-neon-blue">
                {getAverageReactionTime()}
              </p>
            </div>
            <div>
              <p className="text-gray-400 font-tech">Level Progress</p>
              <p className="text-2xl font-cyber font-bold text-neon-green">
                {Math.round((score % (level * 500)) / (level * 500) * 100)}%
              </p>
            </div>
            <div>
              <p className="text-gray-400 font-tech">Next Level</p>
              <p className="text-2xl font-cyber font-bold text-neon-pink">
                {level * 500 - (score % (level * 500))}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Performance Metrics */}
      {isPlaying && (
        <div className="cyber-card p-6">
          <div className="text-center">
            <h4 className="text-xl font-cyber font-bold text-white mb-4">
              Performance Metrics
            </h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="text-center">
                <h5 className="text-lg font-cyber font-bold text-white mb-2">
                  Speed Rating
                </h5>
                <div className="text-3xl font-cyber font-bold">
                  {reactionTime > 0 ? (
                    reactionTime < 200 ? (
                      <span className="text-neon-green">Lightning Fast!</span>
                    ) : reactionTime < 400 ? (
                      <span className="text-neon-blue">Fast</span>
                    ) : reactionTime < 600 ? (
                      <span className="text-neon-yellow">Average</span>
                    ) : (
                      <span className="text-neon-pink">Slow</span>
                    )
                  ) : (
                    <span className="text-gray-400">Waiting...</span>
                  )}
                </div>
              </div>
              
              <div className="text-center">
                <h5 className="text-lg font-cyber font-bold text-white mb-2">
                  Score Multiplier
                </h5>
                <div className="text-3xl font-cyber font-bold text-neon-purple">
                  x{level}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SpeedGame;
